/**
 * CreateQAIUserModal Component
 * Admin modal for creating new Q AI users with initial token balance
 *
 * Endpoint: POST /api/admin/qai/users (FIX Engine - port 7261)
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Alert,
    FormControlLabel,
    Switch,
    Grid,
    InputAdornment,
    IconButton
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    Token as TokenIcon,
    Email as EmailIcon,
    Close as CloseIcon,
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { createQAIUser } from '../../api/admin_api';

// Embedded theme (matches CreditTokensModal)
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.7)', quaternary: 'rgba(255,255,255,0.5)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

// Quick token amounts
const QUICK_AMOUNTS = [50, 100, 250, 500];

const CreateQAIUserModal = ({ open, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        initialTokens: '100',
        isActive: true
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (field) => (e) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }));
        setError(null);
    };

    const handleQuickAmount = (value) => {
        setFormData(prev => ({
            ...prev,
            initialTokens: value.toString()
        }));
    };

    const handleToggleActive = (e) => {
        setFormData(prev => ({
            ...prev,
            isActive: e.target.checked
        }));
    };

    const validateForm = () => {
        if (!formData.email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }
        if (!formData.password) {
            setError('Password is required');
            return false;
        }
        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return false;
        }
        if (!formData.firstName.trim()) {
            setError('First name is required');
            return false;
        }
        if (!formData.lastName.trim()) {
            setError('Last name is required');
            return false;
        }
        if (!formData.initialTokens || isNaN(formData.initialTokens) || parseInt(formData.initialTokens) < 0) {
            setError('Please enter a valid token amount');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        try {
            const result = await createQAIUser({
                email: formData.email.trim(),
                password: formData.password,
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                initialTokens: parseInt(formData.initialTokens),
                isActive: formData.isActive
            });

            if (result.success) {
                toast.success(result.message || `Successfully created Q AI user ${formData.email}`);
                // Reset form
                setFormData({
                    email: '',
                    password: '',
                    firstName: '',
                    lastName: '',
                    initialTokens: '100',
                    isActive: true
                });
                setShowPassword(false);
                onSuccess?.();
            } else {
                setError(result.error || 'Failed to create user');
            }
        } catch (err) {
            console.error('Error creating Q AI user:', err);
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                email: '',
                password: '',
                firstName: '',
                lastName: '',
                initialTokens: '100',
                isActive: true
            });
            setShowPassword(false);
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: theme.colors.bg.primary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: theme.borderRadius.md
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.colors.border.primary}`,
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonAddIcon sx={{ color: theme.colors.accent.tertiary }} />
                    <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        Create Q AI User
                    </Typography>
                </Box>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ minWidth: 'auto', color: theme.colors.text.quaternary }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Email Input */}
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                    Email Address *
                </Typography>
                <TextField
                    fullWidth
                    type="email"
                    placeholder="user@example.com"
                    value={formData.email}
                    onChange={handleChange('email')}
                    InputProps={{
                        startAdornment: <EmailIcon sx={{ color: theme.colors.text.quaternary, mr: 1 }} />
                    }}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        }
                    }}
                />

                {/* Password Input */}
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                    Password *
                </Typography>
                <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 8 characters"
                    value={formData.password}
                    onChange={handleChange('password')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon sx={{ color: theme.colors.text.quaternary }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    sx={{ color: theme.colors.text.quaternary }}
                                >
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    helperText={formData.password && formData.password.length < 8 ? 'Password must be at least 8 characters' : ''}
                    error={formData.password && formData.password.length > 0 && formData.password.length < 8}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        },
                        '& .MuiFormHelperText-root': {
                            color: theme.colors.status.error
                        }
                    }}
                />

                {/* Name Inputs */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                        <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                            First Name *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="John"
                            value={formData.firstName}
                            onChange={handleChange('firstName')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    background: theme.colors.bg.secondary,
                                    color: theme.colors.text.primary,
                                    '& fieldset': { borderColor: theme.colors.border.primary }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                            Last Name *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Doe"
                            value={formData.lastName}
                            onChange={handleChange('lastName')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    background: theme.colors.bg.secondary,
                                    color: theme.colors.text.primary,
                                    '& fieldset': { borderColor: theme.colors.border.primary }
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                {/* Initial Tokens Input */}
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                    Initial Token Balance
                </Typography>
                <TextField
                    fullWidth
                    type="number"
                    placeholder="100"
                    value={formData.initialTokens}
                    onChange={handleChange('initialTokens')}
                    InputProps={{
                        startAdornment: <TokenIcon sx={{ color: theme.colors.text.quaternary, mr: 1 }} />
                    }}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        }
                    }}
                />

                {/* Quick Amount Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    {QUICK_AMOUNTS.map((value) => (
                        <Chip
                            key={value}
                            label={value}
                            onClick={() => handleQuickAmount(value)}
                            sx={{
                                cursor: 'pointer',
                                background: formData.initialTokens === value.toString()
                                    ? theme.colors.accent.primary
                                    : theme.colors.bg.tertiary,
                                color: theme.colors.text.primary,
                                fontWeight: 600,
                                '&:hover': {
                                    background: theme.colors.accent.secondary
                                }
                            }}
                        />
                    ))}
                </Box>

                {/* Active Status Toggle */}
                <Box sx={{
                    p: 2,
                    background: theme.colors.bg.secondary,
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.border.primary}`
                }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.isActive}
                                onChange={handleToggleActive}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: theme.colors.status.success
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: theme.colors.status.success
                                    }
                                }}
                            />
                        }
                        label={
                            <Box>
                                <Typography sx={{ color: theme.colors.text.primary, fontWeight: 500 }}>
                                    Account Active
                                </Typography>
                                <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem' }}>
                                    User can access Q AI immediately after creation
                                </Typography>
                            </Box>
                        }
                    />
                </Box>

                {/* Summary Preview */}
                {formData.email && formData.password && formData.password.length >= 8 && formData.firstName && (
                    <Box sx={{
                        mt: 3,
                        p: 2,
                        background: theme.colors.status.success + '10',
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.status.success}30`
                    }}>
                        <Typography sx={{ color: theme.colors.status.success, fontWeight: 600, mb: 0.5 }}>
                            Ready to Create
                        </Typography>
                        <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.85rem' }}>
                            {formData.firstName} {formData.lastName} ({formData.email}) with {formData.initialTokens || 0} tokens
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                p: 2,
                borderTop: `1px solid ${theme.colors.border.primary}`,
                gap: 1
            }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: theme.colors.text.tertiary }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !formData.email || !formData.password || formData.password.length < 8 || !formData.firstName || !formData.lastName}
                    startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
                    sx={{
                        background: theme.gradients.primaryAccent,
                        '&:hover': { background: theme.colors.accent.secondary },
                        '&:disabled': { background: theme.colors.bg.tertiary }
                    }}
                >
                    {loading ? 'Creating...' : 'Create User'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateQAIUserModal;
